import { h, ComponentChildren, ComponentType } from "preact";
import { useRef, useState } from "preact/hooks";

import { clamp, isInRange } from "../../../foundation/utils/math";
import LibraryButton, { ButtonProps as LibraryButtonProps } from "../Button";
import Menu, { MenuProps } from "../Menu";

import DropdownButton, { DropdownButtonProps } from "./DropdownButton";

import styles from "./DropdownMenu.module.css";

enum HorizontalAligment {
    Auto = "auto",
    Left = "left",
    Right = "right"
}

enum VerticalAligment {
    Auto = "auto",
    Top = "top",
    Bottom = "bottom"
}

type Props = {
    class?: string;
    horizontalAlignment?: HorizontalAligment;
    verticalAlignment?: VerticalAligment;
    horizontalOffset?: number;
    button?: ComponentType<DropdownButtonProps>;
    buttonProps?: Omit<DropdownButtonProps, "activated">;
    actionButtonProps?: LibraryButtonProps;
    menuProps?: Partial<Omit<MenuProps, "onClose">>;
    children: ComponentChildren;
    onMenuStateChange?: (open: boolean) => void;
};

const SCREEN_OFFSET = 4;

export default function DropdownMenu({
    class: cssClass,
    children,
    horizontalAlignment = HorizontalAligment.Auto,
    verticalAlignment = VerticalAligment.Auto,
    horizontalOffset = 0,
    button: Button = DropdownButton,
    buttonProps: {
        onClick,
        ...buttonProps
    } = {},
    actionButtonProps,
    menuProps,
    onMenuStateChange
}: Props) {
    const [menuOpen, setMenuOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    function openMenu() {
        setMenuOpen(true);
        onMenuStateChange?.(true);
    }

    function closeMenu() {
        setMenuOpen(false);
        onMenuStateChange?.(false);
    }

    function handleButtonClick(event: MouseEvent) {
        event.stopPropagation();

        onClick?.(event);
        openMenu();
    }

    function handleActionButtonClick(event: MouseEvent) {
        actionButtonProps?.onClick?.(event);
        closeMenu();
    }

    function handlePositionMenu(menu: HTMLElement) {
        const containerRect = containerRef.current!.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();

        // Horizontal alignment
        const leftScreenBounds = SCREEN_OFFSET;
        const rightScreenBounds = window.innerWidth - menuRect.width - SCREEN_OFFSET;
        const leftAlignedMenuLeft = containerRect.left + horizontalOffset;
        const rightAlignedMenuLeft = containerRect.right - menuRect.width - horizontalOffset;

        const leftAlignmentValid = isInRange(leftAlignedMenuLeft, leftScreenBounds, rightScreenBounds);
        const rightAlignmentValid = isInRange(rightAlignedMenuLeft, leftScreenBounds, rightScreenBounds);

        let calculatedHorizontalAligment;
        if (horizontalAlignment === HorizontalAligment.Right) {
            calculatedHorizontalAligment = HorizontalAligment.Right;
        } else if (horizontalAlignment === HorizontalAligment.Auto && !leftAlignmentValid && rightAlignmentValid) {
            calculatedHorizontalAligment = HorizontalAligment.Right;
        } else {
            calculatedHorizontalAligment = HorizontalAligment.Left;
        }

        const calculatedMenuLeft = calculatedHorizontalAligment === HorizontalAligment.Left
            ? leftAlignedMenuLeft
            : rightAlignedMenuLeft;

        const menuLeft = clamp(calculatedMenuLeft, leftScreenBounds, rightScreenBounds);

        // Vertical alignment
        const topScreenBounds = SCREEN_OFFSET;
        const bottomScreenBounds = window.innerHeight - menuRect.height - SCREEN_OFFSET;
        const topAlignedMenuTop = containerRect.top - menuRect.height - SCREEN_OFFSET;
        const bottomAlignedMenuTop = containerRect.bottom + SCREEN_OFFSET;

        const topAlignmentValid = isInRange(topAlignedMenuTop, topScreenBounds, bottomScreenBounds);
        const bottomAlignmentValid = isInRange(bottomAlignedMenuTop, topScreenBounds, bottomScreenBounds);

        let calculatedVerticalAligment;
        if (verticalAlignment === VerticalAligment.Top) {
            calculatedVerticalAligment = VerticalAligment.Top;
        } else if (verticalAlignment === VerticalAligment.Auto && !bottomAlignmentValid && topAlignmentValid) {
            calculatedVerticalAligment = VerticalAligment.Top;
        } else {
            calculatedVerticalAligment = VerticalAligment.Bottom;
        }

        const calculatedMenuTop = calculatedVerticalAligment === VerticalAligment.Bottom
            ? bottomAlignedMenuTop
            : topAlignedMenuTop;

        const menuTop = clamp(calculatedMenuTop, topScreenBounds, bottomScreenBounds);

        menu.style.transform = `translate3d(${menuLeft}px, ${menuTop}px, 0)`; // TODO: remove direct dom manipulation
    }

    return (
        <div class={cssClass} ref={containerRef}>
            <Button
                {...buttonProps}
                activated={menuOpen}
                onClick={handleButtonClick}/>
            {menuOpen && (
                <Menu.Overlay
                    {...menuProps}
                    footer={actionButtonProps ? (
                        <div class={styles.actionButtonContainer}>
                            <LibraryButton
                                {...actionButtonProps}
                                onClick={handleActionButtonClick}/>
                        </div>
                    ) : undefined}
                    onPositionMenu={handlePositionMenu}
                    onClose={closeMenu}>
                    {children}
                </Menu.Overlay>
            )}
        </div>
    );
}

export {
    Props as DropdownMenuProps,
    HorizontalAligment as DropdownMenuHorizontalAligment,
    VerticalAligment as DropdownMenuVerticalAligment
};
